import { useCallback, useEffect, useMemo, useState } from 'react';

import { Close } from '@mui/icons-material';
import { Button, CircularProgress, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material';

import { useEventBus } from '../../../../hooks/use-event-bus';
import { EventFormsDialog } from '../../../../types/event-bus';

import { useConfig, useNotification, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import { ContactApi } from '../../../../services/api-client/contact-api';
import FormsService from '../../../../services/forms-service/forms-service';
import { PatientDto, PatientInviteMethod } from '../../../../services/forms-service/forms-service-types';
import { checkPatientData } from './helpers';
import { CenterContent, Dialog } from './styles';

export default function ModalSendLink() {
  const [open, setOpen] = useState(false);
  const [patient, setPatient] = useState<PatientDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [type, setType] = useState<'email' | 'sms'>('email');

  const { listen, publish } = useEventBus();
  const { config } = useConfig();
  const oidc = useOidcWithAdmin();
  const { showSuccess, showError } = useNotification();

  const contactApi = useMemo(() => ContactApi.getInstance(), [ContactApi]);
  const formsService = useMemo(() => FormsService.getInstance(config, oidc.oidcService), [config, oidc.oidcService]);

  const initiateSendLink = useCallback(
    async (type: 'email' | 'sms', patientId?: number, patientData?: PatientDto) => {
      try {
        setIsLoading(true);
        setType(type);
        const patient = patientData || (await contactApi.getContactDetailsById(patientId!));

        checkPatientData(patient, publish, type === 'email' ? 'email' : 'phone');

        setPatient(patient);
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        throw error;
      }
    },
    [config, contactApi, publish]
  );

  useEffect(() => {
    return listen('formsDialog', async (event: any) => {
      console.log('formsDialog', event);
      const data = event.data as {
        type: EventFormsDialog['data']['type'];
        patientId?: number;
        patient?: PatientDto;
      };

      if (
        ['send-email-link', 'send-sms-link'].includes(data.type) &&
        'patientId' in data &&
        (data.patient || data.patientId)
      ) {
        setOpen(true);
        try {
          await initiateSendLink(data.type === 'send-email-link' ? 'email' : 'sms', data.patientId, data.patient);
        } catch (error) {
          console.error(error);
          setOpen(false);
        }
      }
    });
  }, [listen, initiateSendLink]);

  const send = useCallback(async () => {
    setOpen(false);
    try {
      await formsService.invite(patient!.id, type === 'email' ? PatientInviteMethod.Email : PatientInviteMethod.SMS);
      showSuccess('The patient form invitation has been successfully sent.');
    } catch (error) {
      console.error(error);
      showError('Problem occurred inviting patient');
    }
  }, [formsService, patient, type, showSuccess, showError]);

  const handleClose = () => {
    setOpen(false);
  };

  const message =
    type === 'email'
      ? `This will send an email invitation to complete the patient's assigned forms${
          patient?.isResponsibleParty ? ', including any family forms' : ''
        }.`
      : `This will send an SMS invitation to complete the patient's assigned forms${
          patient?.isResponsibleParty ? ', including any family forms' : ''
        }.`;

  const title = `Send ${type === 'email' ? 'Email' : 'Text'} Link to ${patient?.firstName || ''} ${
    patient?.lastName || ''
  }`;

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        {title}
        <IconButton sx={{ ml: 'auto' }} onClick={handleClose}>
          <Close />
        </IconButton>
      </DialogTitle>
      {isLoading ? (
        <DialogContent>
          <CenterContent>
            <CircularProgress />
          </CenterContent>
        </DialogContent>
      ) : (
        <DialogContent>{message}</DialogContent>
      )}
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={send} variant="contained">
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
}
